import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
  closestCorners,
  pointerWithin,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '../../lib/utils';

export { arrayMove };

/**
 * SortableRow — a single draggable row for @dnd-kit sortable lists.
 *
 * Renders a grab handle on the left that fades in on hover (Trello-style). Only
 * the handle carries the drag listeners, so inputs/buttons inside `children`
 * stay fully interactive. While dragging, the original row is dimmed to act as
 * the drop placeholder; the lifted copy is rendered by a <DragOverlay>.
 *
 * @param {string|number} props.id            Stable sortable id (must be unique within its SortableContext)
 * @param {React.ReactNode} props.children    Row content, laid out to the right of the handle
 * @param {string} [props.className]          Extra classes for the row wrapper
 * @param {string} [props.handleClassName]    Extra classes for the handle button
 * @param {boolean} [props.disabled]          Disable dragging for this row
 * @param {Object} [props.data]               Arbitrary data attached to the sortable (read in drag handlers)
 */
export function SortableRow({
  id,
  children,
  className,
  handleClassName,
  disabled = false,
  data,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled, data });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/sortable relative flex items-center',
        // The source row becomes the placeholder while its copy is dragged in the overlay
        isDragging && 'opacity-40',
        className
      )}
    >
      {!disabled && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          className={cn(
            'flex-shrink-0 flex items-center justify-center self-stretch',
            'w-6 -ml-1 mr-0.5 rounded text-slate-300',
            'cursor-grab active:cursor-grabbing touch-none',
            'opacity-0 group-hover/sortable:opacity-100 focus-visible:opacity-100',
            'hover:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'transition-opacity',
            handleClassName
          )}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/**
 * DragHandlePlaceholder — spacer matching the handle width, for use inside a
 * <DragOverlay> row (which has no live handle) so the overlay lines up exactly
 * with the source row.
 */
export function DragHandleSpacer({ className }) {
  return <div className={cn('flex-shrink-0 w-6 -ml-1 mr-0.5', className)} aria-hidden="true" />;
}

/**
 * Pointer-first collision detection for dragging between containers: the drop
 * target follows the cursor, so an item changes container the moment the cursor
 * enters it — rather than only once the dragged item's corners are
 * geometrically closest (closestCorners), which forced dragging dramatically
 * past the boundary. Falls back to closestCorners when the pointer isn't over
 * any droppable (e.g. keyboard dragging).
 */
export const pointerFirstCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : closestCorners(args);
};

/**
 * Droppable — a drop target wrapper. Used to make a container (e.g. an empty
 * damage zone) accept drops even when it holds no sortable items. Highlights
 * subtly while a draggable hovers over it.
 */
export function Droppable({ id, data, children, className, activeClassName }) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return (
    <div
      ref={setNodeRef}
      className={cn(className, isOver && (activeClassName || 'rounded-md ring-2 ring-primary/30'))}
    >
      {children}
    </div>
  );
}

/**
 * SortableList — a self-contained, single-container vertical sortable list.
 *
 * Owns its own DndContext, sensors, axis-locked modifiers and a <DragOverlay>
 * that renders the lifted row for a smooth, tactile feel. Use for reordering
 * within one group (e.g. a quote category). For cross-container dragging, wire
 * up DndContext manually.
 *
 * @param {Array<string|number>} props.ids            Ordered sortable ids
 * @param {(activeId, overId) => void} props.onReorder Called on a real move
 * @param {(id) => React.ReactNode} props.renderRow    Row content for id (handle added automatically)
 * @param {(id) => React.ReactNode} [props.renderOverlay] Lifted-row content (defaults to renderRow)
 * @param {string} [props.className]                  Wrapper classes (defaults to a vertical stack)
 * @param {string} [props.rowClassName]               Passed to each SortableRow
 */
export function SortableList({
  ids,
  onReorder,
  renderRow,
  renderOverlay,
  className,
  rowClassName,
}) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    // Small activation distance so clicks/taps on inner inputs aren't hijacked
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(active.id, over.id);
    }
  };

  const overlayContent = renderOverlay || renderRow;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={cn('space-y-2', className)}>
          {ids.map((id) => (
            <SortableRow key={id} id={id} className={rowClassName}>
              {renderRow(id)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        {activeId != null ? (
          <div className="flex items-center rounded-md bg-background shadow-lg ring-1 ring-black/5">
            <div className="flex-shrink-0 w-6 -ml-1 mr-0.5 flex items-center justify-center text-slate-400">
              <GripVertical className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">{overlayContent(activeId)}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableRow;
